import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {NsConfigCheckResultDto} from "../../../../model/dto/api/ns-config-check-result.dto";
import {NsConfigCheckEntryDto} from "../../../../model/dto/api/ns-config-check-entry.dto";

// Severity ordinals from MappingValidationSeverityEnum (AlphaBaseEnum serializes as ordinal)
const SEVERITY_SUCCESS = 0;
const SEVERITY_WARNING = 1;
const SEVERITY_ERROR = 2;

@Component({
    selector: 'ns-wizard-config-check',
    templateUrl: './ns-wizard-config-check.component.html',
    styleUrls: ['./ns-wizard-config-check.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NsWizardConfigCheckComponent implements OnChanges {

    @Input() result: NsConfigCheckResultDto;
    @Input() loading: boolean = false;
    @Input() isCopyMode: boolean = false;
    @Output() onRefresh = new EventEmitter<void>();
    @Output() onNavigateToStep = new EventEmitter<number>();

    // Filter state
    activeFilter: 'all' | 'errors' | 'warnings' = 'all';

    // Pre-computed filtered entries
    filteredEntries: NsConfigCheckEntryDto[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['result']) {
            this.recomputeFilteredEntries();
        }
    }

    refresh(): void {
        this.onRefresh.emit();
    }

    /**
     * Set filter and recompute entries.
     */
    setFilter(filter: 'all' | 'errors' | 'warnings'): void {
        this.activeFilter = filter;
        this.recomputeFilteredEntries();
    }

    // ---------------------------------------------------------------
    // Data Accessors
    // ---------------------------------------------------------------

    /**
     * Recompute filtered entries from result and active filter.
     */
    private recomputeFilteredEntries(): void {
        if (!this.result?.notificationTypes?.length) {
            this.filteredEntries = [];
            return;
        }

        const entries: NsConfigCheckEntryDto[] = [];
        for (const nt of this.result.notificationTypes) {
            if (nt.baseChecks) entries.push(...nt.baseChecks);
            if (nt.templateChecks) entries.push(...nt.templateChecks);
            if (nt.fieldChecks) entries.push(...nt.fieldChecks);
            if (nt.crossChecks) entries.push(...nt.crossChecks);
            if (nt.channelChecks) {
                for (const ch of nt.channelChecks) {
                    if (ch.checks) entries.push(...ch.checks);
                }
            }
        }

        if (this.activeFilter === 'errors') {
            this.filteredEntries = entries.filter(e => e.severity === SEVERITY_ERROR);
        } else if (this.activeFilter === 'warnings') {
            this.filteredEntries = entries.filter(e => e.severity === SEVERITY_WARNING);
        } else {
            this.filteredEntries = entries;
        }
    }

    // ---------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------

    getSeverityIcon(severity: any): string {
        switch (severity) {
            case SEVERITY_ERROR:
                return 'fa-solid fa-circle-xmark text-red-500';
            case SEVERITY_WARNING:
                return 'fa-solid fa-triangle-exclamation text-yellow-500';
            case SEVERITY_SUCCESS:
                return 'fa-solid fa-circle-check text-green-500';
            default:
                return 'fa-solid fa-circle text-color-secondary';
        }
    }

    getSeverityTagSeverity(severity: any): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" {
        switch (severity) {
            case SEVERITY_ERROR:
                return 'danger';
            case SEVERITY_WARNING:
                return 'warn';
            case SEVERITY_SUCCESS:
                return 'success';
            default:
                return 'secondary';
        }
    }

    getStepForGroup(group: string): number | null {
        switch (group) {
            case 'NOTIFICATION_TYPE_BASE':
            case 'BASE':
                return 1;
            case 'CHANNEL_BASE':
            case 'CHANNEL':
            case 'CHANNEL_TEMPLATE':
            case 'TEMPLATE':
                return 2;
            case 'FIELD':
            case 'VARIABLE':
                return 2.5;
            default:
                return null;
        }
    }

    navigateToStep(group: string): void {
        const step = this.getStepForGroup(group);
        if (step !== null) {
            this.onNavigateToStep.emit(step);
        }
    }

    getGroupLabel(group: string): string {
        switch (group) {
            case 'BASE':
                return 'Basisdaten';
            case 'CHANNEL':
                return 'Kanäle';
            case 'TEMPLATE':
                return 'Vorlagen';
            case 'VARIABLE':
                return 'Variablen';
            case 'FIELD':
                return 'Felder';
            case 'CROSS':
                return 'Übergreifend';
            default:
                return group || 'Allgemein';
        }
    }
}
